/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled from "styled-components";

import useWidth from "@src/components/modules/LiveModule/useWidth";
import { LIVE } from "@src/components/modules/LiveModule/liveUtils";

/*
 * The dashboard's charts, plain SVG in the Ops Console style: thin bars with rounded tops on a
 * recessive grid, a 2 px surface gap between stacked segments, a legend, and a tooltip per bar.
 * Status series use the reserved status colours (validated with the dataviz checks); categories
 * use the Ops chart palette in a fixed order.
 */

// Brand series only (UI spec 8.1): was the multicolour set "#3B82F6", "#10B981", "#F59E0B",
// "#EF4444", "#8B5CF6", "#06B6D4", and traffic-light status colours (green / blue / red).
export const CHART_PALETTE = [
  "var(--t9-series-1)",
  "var(--t9-series-2)",
  "var(--t9-series-3)",
  "var(--t9-series-4)",
  "var(--t9-series-5)",
  "var(--t9-series-6)",
];
export const STATUS_COLOURS = {
  completed: "var(--t9-primary)",
  running: "var(--t9-accent-500)",
  failed: "var(--t9-brand-900)",
};

const Frame = styled.div`
  position: relative;
  width: 100%;
`;
const Tip = styled.div`
  position: absolute;
  pointer-events: none;
  z-index: 5;
  background: var(--t9-tooltip-bg);
  color: var(--t9-tooltip-text);
  font-size: 12px;
  line-height: 17px;
  padding: 7px 10px;
  border-radius: 6px;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
  transform: translate(-50%, calc(-100% - 8px));
  b {
    font-weight: 600;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .sw {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }
`;
const Legend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 12px;
  color: ${LIVE.textSecondary};
  span.i {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  span.sw {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
`;

export type Series = { key: string; label: string; colour: string };

/** Rounded-top bar path (4 px radius, square at the baseline). */
const barPath = (x: number, y: number, w: number, h: number, r = 4) => {
  if (h <= 0) return "";
  const rr = Math.min(r, w / 2, h);
  return `M ${x} ${y + h} V ${y + rr} Q ${x} ${y} ${x + rr} ${y} H ${x + w - rr} Q ${x + w} ${y} ${x + w} ${y + rr} V ${y + h} Z`;
};

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * mag;
};

type ColumnsProps = {
  labels: string[];
  /** One value per column per series (stacked bottom-up in series order). */
  values: { [series: string]: number[] };
  series: Series[];
  height?: number;
  formatValue?: (v: number) => string;
  /** Axis tick label under every n-th column. */
  every?: number;
  ariaLabel: string;
  showLegend?: boolean;
};

export const StackedColumns = ({
  labels,
  values,
  series,
  height = 180,
  formatValue = v => String(Math.round(v)),
  every = 7,
  ariaLabel,
  showLegend = true,
}: ColumnsProps) => {
  const [ref, width] = useWidth(600);
  const [hover, setHover] = React.useState<number | null>(null);
  const bottom = 22;
  const top = 8;
  const n = labels.length;
  const totals = labels.map((_, i) => series.reduce((a, s) => a + (values[s.key]?.[i] || 0), 0));
  const max = niceMax(Math.max(...totals, 0));
  const longest = Math.max(...[0, max / 2, max].map(v => formatValue(v).length));
  const left = Math.max(28, Math.round(longest * 6.6) + 14);
  const plotW = Math.max(10, width - left - 4);
  const plotH = height - bottom - top;
  const slot = plotW / Math.max(1, n);
  const barW = Math.max(3, Math.min(22, slot * 0.62));
  const ticks = [0, 0.5, 1].map(f => f * max);
  const y = (v: number) => top + plotH - (v / max) * plotH;

  return (
    <Frame ref={ref}>
      <svg width={width} height={height} role="img" aria-label={ariaLabel} style={{ display: "block" }}>
        {ticks.map(t => (
          <g key={t}>
            <line x1={left} x2={width - 4} y1={y(t)} y2={y(t)} stroke={t === 0 ? "var(--t9-border-control)" : "var(--t9-bg-sunken)"} strokeWidth={1} />
            <text x={left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill={LIVE.faint}>
              {formatValue(t)}
            </text>
          </g>
        ))}
        {labels.map((label, i) => {
          const cx = left + slot * i + slot / 2;
          let acc = 0;
          const segs = series.map(s => {
            const v = values[s.key]?.[i] || 0;
            const y0 = y(acc);
            acc += v;
            const y1 = y(acc);
            return { s, v, y0, y1 };
          });
          const visible = segs.filter(sg => sg.v > 0);
          return (
            <g key={label + i}>
              {visible.map((sg, k) => {
                const isTop = k === visible.length - 1;
                const gap = k > 0 ? 2 : 0; // surface gap between stacked segments
                const h = Math.max(0, sg.y0 - sg.y1 - gap);
                return isTop ? (
                  <path key={sg.s.key} d={barPath(cx - barW / 2, sg.y1, barW, h)} fill={sg.s.colour} opacity={hover == null || hover === i ? 1 : 0.55} />
                ) : (
                  <rect key={sg.s.key} x={cx - barW / 2} y={sg.y1} width={barW} height={h} fill={sg.s.colour} opacity={hover == null || hover === i ? 1 : 0.55} />
                );
              })}
              {i % every === (n - 1) % every ? (
                <text x={cx} y={height - 6} textAnchor="middle" fontSize={11} fill={LIVE.faint}>
                  {label}
                </text>
              ) : null}
              <rect
                x={left + slot * i}
                y={top}
                width={slot}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hover != null ? (
        <Tip style={{ left: left + slot * hover + slot / 2, top: y(totals[hover]) }}>
          <b>{labels[hover]}</b>
          {series.map(s => (
            <div className="row" key={s.key}>
              <span className="sw" style={{ background: s.colour }} />
              {s.label}: <b>{formatValue(values[s.key]?.[hover] || 0)}</b>
            </div>
          ))}
        </Tip>
      ) : null}
      {showLegend && series.length > 1 ? (
        <Legend>
          {series.map(s => (
            <span className="i" key={s.key}>
              <span className="sw" style={{ background: s.colour }} />
              {s.label}
            </span>
          ))}
        </Legend>
      ) : null}
    </Frame>
  );
};

type DonutProps = {
  items: { label: string; value: number }[];
  centre: string;
  centreLabel: string;
  ariaLabel: string;
};

const DonutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;
const DonutList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  flex-grow: 1;
  min-width: 0;
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${LIVE.textSecondary};
    padding: 5px 0;
    border-bottom: 1px solid var(--t9-bg-sunken);
  }
  li:last-child {
    border-bottom: none;
  }
  .sw {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }
  .l {
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .v {
    font-weight: 600;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
  }
`;

export const Donut = ({ items, centre, centreLabel, ariaLabel }: DonutProps) => {
  const [hover, setHover] = React.useState<number | null>(null);
  const size = 140;
  const r = 56;
  const stroke = 16;
  const total = items.reduce((a, i) => a + i.value, 0) || 1;
  const circ = 2 * Math.PI * r;
  const gap = items.length > 1 ? 2 : 0;
  let offset = 0;
  // Beyond six categories the rest fold into "Other" (no generated hues).
  const shown = items.length > 6 ? [...items.slice(0, 5), { label: "Other", value: items.slice(5).reduce((a, i) => a + i.value, 0) }] : items;
  return (
    <DonutRow>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel} style={{ flexShrink: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--t9-bg-sunken)" strokeWidth={stroke} />
        {shown.map((it, i) => {
          const len = (it.value / total) * circ;
          const seg = (
            <circle
              key={it.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
              strokeWidth={hover === i ? stroke + 3 : stroke}
              strokeDasharray={`${Math.max(0, len - gap)} ${circ}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <title>{`${it.label}: ${it.value}`}</title>
            </circle>
          );
          offset += len;
          return seg;
        })}
        <text x={size / 2} y={size / 2 + 2} textAnchor="middle" fontSize={26} fontWeight={700} fill={LIVE.text}>
          {hover != null ? shown[hover].value : centre}
        </text>
        <text x={size / 2} y={size / 2 + 20} textAnchor="middle" fontSize={11} fill={LIVE.muted}>
          {hover != null ? shown[hover].label : centreLabel}
        </text>
      </svg>
      <DonutList>
        {shown.map((it, i) => (
          <li key={it.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <span className="sw" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
            <span className="l">{it.label}</span>
            <span className="v">{it.value}</span>
          </li>
        ))}
      </DonutList>
    </DonutRow>
  );
};
